import React, { useState, useEffect } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Pencil, Loader2, AlertCircle, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@cms/ui/components/dialog";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { formatTimestamp } from "@cms/portal-lib/format";
import { PageHeader } from "@cms/ui/components/page-header";
import { MediaHeader } from "../components/media-header";
import { fetchGalleries, createGallery, deleteGallery, reorderGalleries, type Gallery } from "../lib/galleries";
import { moveItem } from "../lib/menus";
import type { ClientOutletContext } from "../lib/outlet-context";

export function GalleriesPage() {
  const { api, refreshStatus } = useOutletContext<ClientOutletContext>();
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState<Gallery[] | null>(null);
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<Gallery | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (!api) return;
    fetchGalleries(api)
      .then((data) => setGalleries(data.galleries))
      .catch((err: Error) => setError(err.message));
  }, [api]);

  const openCreate = () => {
    setNewName("");
    setCreateError("");
    setCreateOpen(true);
  };

  const create = async () => {
    if (!api) return;
    if (!newName.trim()) {
      setCreateError("Enter a gallery name.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const result = await createGallery(api, newName.trim());
      setCreateOpen(false);
      refreshStatus?.();
      navigate(`/media/galleries/${result.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deleteGallery(api, deleteTarget.id);
      setGalleries((prev) => (prev ? prev.filter((g) => g.id !== deleteTarget.id) : prev));
      setDeleteTarget(null);
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  // Reorder persists immediately — optimistic update, rolled back on failure.
  const move = async (index: number, delta: -1 | 1) => {
    if (!api || !galleries) return;
    const next = moveItem(galleries, index, delta);
    if (next === galleries) return;
    const previous = galleries;
    setGalleries(next);
    setReordering(true);
    setError("");
    try {
      await reorderGalleries(api, next.map((g) => g.id));
      refreshStatus?.();
    } catch (err) {
      setGalleries(previous);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReordering(false);
    }
  };

  return (
    <div>
      <MediaHeader />
      <PageHeader
        title="Galleries"
        description="Create photo galleries from your media library, then add them to any page with the Gallery block in the page editor."
        actions={
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4" />
            Create gallery
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!galleries && !error && <LoadingSpinner className="py-20" />}

      {galleries && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Images</th>
                  <th className="px-4 py-3 font-medium">Last updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {galleries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      No galleries yet — create your first gallery to get started.
                    </td>
                  </tr>
                )}
                {galleries.map((gallery, index) => (
                  <tr key={gallery.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{gallery.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {gallery.images.length} {gallery.images.length === 1 ? "image" : "images"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatTimestamp(gallery.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === 0 || reordering}
                          aria-label="Move up"
                          onClick={() => move(index, -1)}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === galleries.length - 1 || reordering}
                          aria-label="Move down"
                          onClick={() => move(index, 1)}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/media/galleries/${gallery.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          title="Delete gallery"
                          onClick={() => setDeleteTarget(gallery)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create gallery */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a gallery</DialogTitle>
            <DialogDescription>
              Give your gallery a name, then add photos. Show it on any page with the Gallery block.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-gallery-name">Name</Label>
              <Input
                id="new-gallery-name"
                value={newName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && create()}
                placeholder="e.g. Our Work"
                maxLength={100}
                autoFocus
              />
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={create} disabled={creating || !newName.trim()}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={
          <>
            This deletes the gallery ({deleteTarget?.images.length ?? 0} image
            {deleteTarget?.images.length === 1 ? "" : "s"}). The photos stay in your media
            library. Pages using this gallery show nothing in its place. This can't be undone.
          </>
        }
        confirmLabel="Delete gallery"
        busy={deleting}
        onConfirm={remove}
      />
    </div>
  );
}
